/**
 * Runtime renderer for every supported field type.
 *
 * This component is used for public submission, edit-submission and read-only
 * report modes. It applies safe custom attributes, validation messages and
 * Bootstrap compatibility classes without executing legacy AngularJS markup.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { getCaptchaUrl, validateCaptcha } from '../../api/forms-api';
import { readRuntimeUiLocale, useUiLocale, type UiLocale } from '../../app/ui-locale';
import { useFeedback } from '../../components/Feedback';
import type { FormField, FormRow, FormValues } from '../../models/form';
import { getValueByPath, evaluateVisibility } from '../../utils/conditions';
import { parseInlineStyle, safeControlAttributes, safeWrapperAttributes, sanitizeCustomHtml } from '../../utils/customization';
import { normalizeNumericText, toEnglishNumbers } from '../../utils/numbers';
import { hasVisibleFieldTitle, normalizedFieldTitle, renderFieldLabel } from '../../utils/field-label';
import { applyLegacyMask } from '../../utils/mask';
import { validateFieldSync, type ValidationError } from '../../utils/validation';
import { normalizeHttpUrl, normalizeIranianMobile, sanitizeIranianMobileInput } from '../../utils/input-normalization';
import { PersianDateTimeInput } from './PersianDateTimeInput';
import { CalculatedFieldInput } from './controls/CalculatedFieldInput';
import { LookupInput } from './controls/LookupInput';
import { FilePreviewItem, FileUploadControl, fileUrl, type SubmissionFileAccess } from './controls/FileUploadControl';
import { isMultipleFileField, parseFileValues } from '../../utils/file-values';
import { displayPresetInput, getInputPreset, normalizePresetInput } from '../../utils/input-presets';
import {
  clearSimpleCaptchaChallenge,
  getSimpleCaptchaChallenge,
  markSimpleCaptchaVerified,
  createSimpleCaptchaChallengeId,
  normalizeSimpleCaptchaInput,
  registerSimpleCaptchaChallenge,
  requestSimpleCaptchaRefresh,
  resolveSimpleCaptchaDigitStyle,
  resolveSimpleCaptchaLength,
  resolveSimpleCaptchaMode,
  resolveSimpleCaptchaVisualStyle,
  subscribeSimpleCaptchaRefresh,
  updateSimpleCaptchaAnswer
} from '../../utils/simple-captcha';
import { publicCopy, type PublicCopy } from './public-copy';
import { resolveFieldAlignment } from '../../utils/field-alignment';

interface Props {
  field: FormField;
  tabName: string;
  value: unknown;
  values: FormValues;
  errors?: ValidationError[];
  errorMap?: Record<string, ValidationError[]>;
  readOnly?: boolean;
  onChange: (value: unknown) => void;
  onFieldChange?: (name: string, value: unknown) => void;
  onAction?: (action: NonNullable<FormField['buttonAction']>) => void;
  interactionDisabled?: boolean;
  secureSubmissionId?: number;
  submissionFileAccess?: SubmissionFileAccess;
  omitVerificationFields?: boolean;
}

function errorPath(tabName: string, fieldName: string): string { return `${tabName}.${fieldName}`; }


function fieldClass(field: FormField): string {
  const alignment = resolveFieldAlignment(field, readRuntimeUiLocale());
  return [field.cssClass, `text-${alignment}`, field.size ? `df-field-${field.size}` : '']
    .filter(Boolean)
    .join(' ');
}

function controlClass(field: FormField, base = ''): string {
  return [base, fieldClass(field), field.customization?.controlClass].filter(Boolean).join(' ');
}

function applyDisplayFilter(value: unknown, filter: string, locale: UiLocale, culture: string): string {
  const text = String(value ?? '');
  if (filter === 'uppercase') return text.toUpperCase();
  if (filter === 'lowercase') return text.toLowerCase();
  if (filter === 'number') {
    const number = Number(toEnglishNumbers(text));
    return Number.isFinite(number) ? number.toLocaleString(culture) : text;
  }
  if (filter === 'date') {
    const date = new Date(text);
    return Number.isNaN(date.valueOf())
      ? text
      : date.toLocaleDateString(culture, locale === 'fa' ? { timeZone: 'Asia/Tehran' } : undefined);
  }
  return text;
}

function ValidationMessages({ errors, id }: { errors?: ValidationError[]; id?: string }) {
  if (!errors?.length) return null;
  return <div id={id} className="help-block invalid-feedback d-block df-validation-errors" role="alert">{errors.map((error, index) => <p key={`${error.key}-${index}`}>{error.message}</p>)}</div>;
}

function CustomHtml({ html, position }: { html?: string; position: 'before' | 'after' }) {
  if (!html?.trim()) return null;
  return <div className={`df-custom-html df-custom-html--${position}`} dangerouslySetInnerHTML={{ __html: sanitizeCustomHtml(html) }} />;
}

function isFileUploadField(field: FormField): boolean {
  return field.type === 'fileupload'
    || (field.type === 'input' && String(field.inputType ?? '').toLowerCase() === 'file');
}

const IMAGE_FILE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']);
const VIDEO_FILE_EXTENSIONS = new Set(['mp4', 'webm', 'ogg', 'mov']);
const AUDIO_FILE_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'm4a']);

function fileNameText(value: unknown): string {
  return String(value ?? '').trim();
}

function getFileExtension(fileName: string): string {
  const withoutQuery = fileName.split('?')[0] ?? fileName;
  const clean = withoutQuery.split('#')[0] ?? withoutQuery;
  const index = clean.lastIndexOf('.');
  return index >= 0 ? clean.slice(index + 1).toLowerCase() : '';
}

function getFileUrl(fileName: string): string {
  const clean = fileName.trim();
  if (/^(?:https?:)?\/\//i.test(clean) || clean.startsWith('/')) return clean;
  return `/DesktopModules/FaraDynamicForms/UserMedia/${encodeURIComponent(clean)}`;
}

function ensureWebUrl(raw: string): string {
  if (!raw) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

function getOptionLabel(field: FormField, rawValue: unknown): string {
  const current = String(rawValue ?? '');
  return field.options?.find((option) => option.value === current)?.label ?? current;
}

function FileValueView({ fileName, compact = false }: { fileName: string; compact?: boolean }) {
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const safeName = fileNameText(fileName);
  if (!safeName) return <span className="df-readonly-empty">—</span>;
  const ext = getFileExtension(safeName);
  const url = getFileUrl(safeName);
  const isImage = IMAGE_FILE_EXTENSIONS.has(ext);
  const isVideo = VIDEO_FILE_EXTENSIONS.has(ext);
  const isAudio = AUDIO_FILE_EXTENSIONS.has(ext);
  const documentCode = ext ? ext.toUpperCase() : 'FILE';

  return <div className={['df-file-preview-card', compact ? 'df-file-preview-card--compact' : '', isImage ? 'df-file-preview-card--image' : 'df-file-preview-card--document'].filter(Boolean).join(' ')}>
    <div className="df-file-preview-visual">
      {isImage
        ? <a href={url} target="_blank" rel="noreferrer" className="df-file-preview-thumb"><img src={url} alt={copy.attachmentImageAlt} loading="lazy" /></a>
        : isVideo
          ? <video className="df-file-preview-media" controls preload="metadata"><source src={url} /></video>
          : isAudio
            ? <audio className="df-file-preview-audio" controls preload="metadata"><source src={url} /></audio>
            : <a href={url} target="_blank" rel="noreferrer" className="df-file-preview-thumb df-file-preview-thumb--document"><span className="df-file-icon">{documentCode}</span></a>}
    </div>
    <div className="df-file-preview-meta">
      <div className="df-file-preview-name" title={safeName}>{safeName}</div>
      <div className="df-file-preview-actions no-print">
        <a href={url} target="_blank" rel="noreferrer" className="btn btn-default btn-xs">{copy.view}</a>
        <a href={url} download={safeName} className="btn btn-success btn-xs">{copy.download}</a>
      </div>
      <div className="df-file-preview-print-link print-only"><span className="df-file-chip">{documentCode}</span> <a href={url} target="_blank" rel="noreferrer">{safeName}</a></div>
    </div>
  </div>;
}

function ReadOnlyValue({ field, value, secureSubmissionId = 0, submissionFileAccess = 'owner' }: { field: FormField; value: unknown; secureSubmissionId?: number; submissionFileAccess?: SubmissionFileAccess }) {
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const alignmentClass = `text-${resolveFieldAlignment(field, locale)}`;
  if (isFileUploadField(field)) return <FileUploadControl field={field} value={value} readOnly disabled secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} controlId={`df-readonly-${field.id}`} className="" attributes={{}} onChange={() => undefined} />;
  if (field.type === 'checkbox') {
    const checked = value === true || String(value ?? '').toLowerCase() === 'true';
    return <div className={`df-readonly-value ${alignmentClass}`}>{checked ? copy.yes : copy.no}</div>;
  }
  if (field.type === 'radio' || field.type === 'select') {
    const optionText = getOptionLabel(field, value);
    return <div className={`df-readonly-value ${alignmentClass}`}>{optionText || <span className="df-readonly-empty">—</span>}</div>;
  }
  const plainText = String(value ?? '').trim();
  if (field.inputType === 'url' && plainText) {
    return <div className={`df-readonly-value dir-ltr ${alignmentClass}`}><a href={ensureWebUrl(plainText)} target="_blank" rel="noreferrer">{plainText}</a></div>;
  }
  const multiline = field.type === 'textarea' || plainText.includes('\n');
  const ltr = field.inputType === 'email' || field.inputType === 'url' || field.inputType === 'tel';
  return <div className={['df-readonly-value', multiline ? 'df-readonly-value--multiline' : '', ltr ? 'dir-ltr' : '', alignmentClass].filter(Boolean).join(' ')}>{plainText || <span className="df-readonly-empty">—</span>}</div>;
}

function TableDraftInput({ field, value, errors, disabled, onChange }: { field: FormField; value: unknown; errors?: ValidationError[]; disabled?: boolean; onChange: (value: unknown) => void }) {
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const invalid = Boolean(errors?.length);
  if (field.type === 'simplecaptcha') {
    return <><SimpleCaptchaField
      field={field}
      value={value}
      onChange={onChange}
      readOnly={false}
      interactionDisabled={Boolean(disabled)}
      controlId={`df-table-captcha-${field.id}`}
      className={invalid ? 'is-invalid' : undefined}
      attributes={{}}
    /><ValidationMessages errors={errors} /></>;
  }
  if (field.type === 'select') {
    return <><select className={`form-control form-select ${invalid ? 'is-invalid' : ''}`} value={String(value ?? '')} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
      <option value="">{field.placeholder || copy.selectPlaceholder}</option>
      {field.options?.map((option) => <option key={option.id} value={option.value} disabled={option.disabled}>{option.label}</option>)}
    </select><ValidationMessages errors={errors} /></>;
  }
  if (field.type === 'radio') {
    return <><div className={`df-choice-list df-choice-list--radio ${fieldClass(field)}`}>{field.options?.map((option) => <div className="radio form-check df-radio" key={option.id}><label className="form-check-label"><input className="form-check-input" type="radio" value={option.value} checked={String(value ?? '') === option.value} disabled={disabled || option.disabled} onChange={(event) => onChange(event.target.value)} /> <span>{option.label}</span></label></div>)}</div><ValidationMessages errors={errors} /></>;
  }
  if (field.type === 'checkbox') {
    const checked = value === true || String(value ?? '').toLowerCase() === 'true';
    return <><div className={`checkbox form-check df-check ${fieldClass(field)}`}><label className="form-check-label"><input className="form-check-input" type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked ? 'True' : 'False')} /> <span>{field.title}</span></label></div><ValidationMessages errors={errors} /></>;
  }
  if (field.type === 'textarea') {
    return <><textarea className={`form-control ${fieldClass(field)} ${invalid ? 'is-invalid' : ''}`} rows={2} value={String(value ?? '')} placeholder={field.placeholder} disabled={disabled} onChange={(event) => onChange(event.target.value)} /><ValidationMessages errors={errors} /></>;
  }
  if (isFileUploadField(field)) {
    return <><FileUploadControl
      field={field}
      value={value}
      readOnly={false}
      disabled={Boolean(disabled)}
      controlId={`df-table-file-${field.id}`}
      className={`form-control df-file-input ${invalid ? 'is-invalid' : ''}`}
      attributes={{}}
      compact
      onChange={onChange}
    /><ValidationMessages errors={errors} /></>;
  }

  const special = field.inputType === 'tdate' || field.inputType === 'ttime' || field.inputType === 'tdatetime';
  if (special) {
    return <><PersianDateTimeInput
      id={`df-table-draft-${field.id}`}
      name={field.name}
      kind={field.inputType as 'tdate' | 'ttime' | 'tdatetime'}
      value={value}
      placeholder={field.placeholder}
      disabled={disabled}
      className={`form-control ${fieldClass(field)} ${invalid ? 'is-invalid' : ''}`}
      ariaInvalid={invalid}
      onChange={onChange}
    /><ValidationMessages errors={errors} /></>;
  }

  const preset = getInputPreset(field.inputPreset);
  const numeric = field.inputType === 'number';
  const mobile = field.inputType === 'tel' || field.inputPreset === 'iranMobile';
  const url = field.inputType === 'url';
  const forceLtr = field.inputPreset === 'iranCardNumber' || field.inputPreset === 'iranIban';
  return <><input
    className={`form-control ${fieldClass(field)} ${forceLtr ? 'dir-ltr df-preset-ltr' : ''} ${invalid ? 'is-invalid' : ''}`}
    dir={forceLtr ? 'ltr' : undefined}
    type={numeric ? 'number' : field.inputType === 'email' ? 'email' : mobile ? 'tel' : field.inputType === 'password' ? 'password' : 'text'}
    min={numeric ? field.validation?.minValue : undefined}
    max={numeric ? field.validation?.maxValue : undefined}
    step={numeric ? field.validation?.step ?? 'any' : undefined}
    inputMode={preset?.inputMode ?? (numeric ? 'decimal' : mobile ? 'tel' : url ? 'url' : undefined)}
    maxLength={preset?.displayMaxLength ?? preset?.maxLength ?? (mobile ? 14 : undefined)}
    autoComplete={preset?.autoComplete ?? (mobile ? 'tel' : url ? 'url' : field.inputType === 'password' ? 'current-password' : undefined)}
    spellCheck={url ? false : undefined}
    value={field.inputPreset ? displayPresetInput(value, field.inputPreset) : numeric ? normalizeNumericText(value) : String(value ?? '')}
    placeholder={field.placeholder}
    disabled={disabled}
    onBlur={(event) => {
      if (field.inputPreset) onChange(normalizePresetInput(event.currentTarget.value, field.inputPreset));
      else if (mobile) onChange(normalizeIranianMobile(event.currentTarget.value));
      else if (url && event.currentTarget.value.trim()) onChange(normalizeHttpUrl(event.currentTarget.value));
    }}
    onChange={(event) => onChange(field.inputPreset
      ? normalizePresetInput(event.target.value, field.inputPreset)
      : numeric
        ? normalizeNumericText(event.target.value)
        : mobile
          ? sanitizeIranianMobileInput(event.target.value)
          : event.target.value)}
  /><ValidationMessages errors={errors} /></>;
}

function renderTableValue(column: FormField, value: unknown, readOnly: boolean, copy: PublicCopy, secureSubmissionId = 0, submissionFileAccess: SubmissionFileAccess = 'owner'): ReactNode {
  if (readOnly) return <ReadOnlyValue field={column} value={value} secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} />;
  if (column.type === 'checkbox') return String(value ?? '').toLowerCase() === 'true' ? copy.yes : copy.no;
  if (isFileUploadField(column) && value) {
    const files = parseFileValues(value, isMultipleFileField(column));
    return <div className="df-table-file-values">{files.map((name, index) => <FilePreviewItem fileName={name} compact secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} key={`${name}-${index}`} />)}</div>;
  }
  if (column.type === 'select' || column.type === 'radio') return getOptionLabel(column, value);
  return String(value ?? '');
}

const TABLE_VALUE_FIELD_TYPES = new Set<FormField['type']>(['input', 'textarea', 'select', 'checkbox', 'radio', 'fileupload']);
const TABLE_DRAFT_MODEL_FIELD_TYPES = new Set<FormField['type']>([...TABLE_VALUE_FIELD_TYPES, 'captcha', 'simplecaptcha']);

function inheritTableRequired(field: FormField, required: boolean): FormField {
  return required && TABLE_VALUE_FIELD_TYPES.has(field.type)
    ? { ...field, validation: { ...(field.validation ?? {}), required: true } }
    : field;
}

function collectFieldsFromRows(rows: FormRow[] | undefined, predicate: (field: FormField) => boolean): FormField[] {
  const output: FormField[] = [];
  const visitFields = (fields: FormField[]) => {
    for (const field of fields) {
      if (predicate(field)) output.push(field);
      if (field.children?.length) visitFields(field.children);
      if (field.panelRows?.length) visitRows(field.panelRows);
      // Nested tables are intentionally not flattened into the parent row.
    }
  };
  const visitRows = (items: FormRow[]) => {
    for (const row of items) {
      for (const column of row.columns) {
        visitFields(column.fields);
        if (column.rows?.length) visitRows(column.rows);
      }
    }
  };
  if (rows?.length) visitRows(rows);
  return output;
}

function uniqueTableFields(fields: FormField[]): FormField[] {
  const seen = new Set<string>();
  return fields.filter((field) => {
    const key = field.name || field.id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function tableOutputColumns(field: FormField): FormField[] {
  return uniqueTableFields([
    ...(field.tableColumns ?? []).filter((column) => TABLE_VALUE_FIELD_TYPES.has(column.type)),
    ...collectFieldsFromRows(field.tableRows, (candidate) => TABLE_VALUE_FIELD_TYPES.has(candidate.type))
  ]);
}

function tableDraftFields(field: FormField): FormField[] {
  return uniqueTableFields([
    ...(field.tableColumns ?? []).filter((column) => TABLE_DRAFT_MODEL_FIELD_TYPES.has(column.type)),
    ...collectFieldsFromRows(field.tableRows, (candidate) => TABLE_DRAFT_MODEL_FIELD_TYPES.has(candidate.type))
  ]);
}

function TableDraftRows({ rows, tableId, tabName, draft, values, errors, disabled, requiredByTable, onDraftChange, onAction }: {
  rows: FormRow[];
  tableId: string;
  tabName: string;
  draft: Record<string, unknown>;
  values: FormValues;
  errors: Record<string, ValidationError[]>;
  disabled: boolean;
  requiredByTable: boolean;
  onDraftChange: (name: string, value: unknown) => void;
  onAction: (action: NonNullable<FormField['buttonAction']>) => void;
}) {
  const mergedValues: FormValues = {
    ...values,
    [tabName]: { ...(values[tabName] ?? {}), ...draft }
  };
  const errorMap = Object.fromEntries(Object.entries(errors).map(([name, items]) => [errorPath(tabName, name), items]));
  return <div className="df-table-draft-layouts" data-table-id={tableId}>{rows.map((row) => <div className="row df-form-row df-table-draft-row" key={row.id}>
    {row.columns.map((column) => <div className={`col-md-${column.span} df-form-col df-col-span-${column.span}`} key={column.id}>
      {column.fields.map((child) => {
        const effectiveChild = inheritTableRequired(child, requiredByTable);
        return <FieldRenderer
          key={child.id}
          field={effectiveChild}
          tabName={tabName}
          value={draft[child.name]}
          values={mergedValues}
          errors={errors[child.name]}
          errorMap={errorMap}
          readOnly={false}
          onChange={(next) => onDraftChange(child.name, next)}
          onFieldChange={onDraftChange}
          onAction={onAction}
          interactionDisabled={disabled}
        />;
      })}
      {column.rows?.length ? <TableDraftRows rows={column.rows} tableId={tableId} tabName={tabName} draft={draft} values={values} errors={errors} disabled={disabled} requiredByTable={requiredByTable} onDraftChange={onDraftChange} onAction={onAction} /> : null}
    </div>)}
  </div>)}</div>;
}

function TableField({ field, value, tabName, values, errors: tableErrors, readOnly, interactionDisabled, onChange, onAction, secureSubmissionId, submissionFileAccess }: Pick<Props, 'field' | 'value' | 'tabName' | 'values' | 'errors' | 'readOnly' | 'interactionDisabled' | 'onChange' | 'onAction' | 'secureSubmissionId' | 'submissionFileAccess'>) {
  const { notify } = useFeedback();
  const { locale, formatNumber } = useUiLocale();
  const copy = publicCopy(locale);
  const rows = Array.isArray(value) ? value as Array<Record<string, unknown>> : [];
  const tableRequiresCompleteRows = Boolean(field.validation?.required);
  const directColumns = (field.tableColumns ?? []).map((column) => inheritTableRequired(column, tableRequiresCompleteRows));
  const columns = tableOutputColumns(field);
  const draftModelFields = tableDraftFields(field).map((column) => inheritTableRequired(column, tableRequiresCompleteRows));
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [draftErrors, setDraftErrors] = useState<Record<string, ValidationError[]>>({});
  const disabled = Boolean(readOnly || interactionDisabled || field.disabled);
  const maximumRows = field.validation?.maximumRows && field.validation.maximumRows > 0 ? field.validation.maximumRows : undefined;
  const atMaximumRows = maximumRows != null && rows.length >= maximumRows;
  const showInheritedRequiredErrors = rows.length === 0 && Boolean(tableErrors?.some((item) => item.key === 'required'));
  const displayedDraftErrors = showInheritedRequiredErrors
    ? draftModelFields.reduce<Record<string, ValidationError[]>>((output, column) => {
      const current = draftErrors[column.name] ?? [];
      const requiredErrors = validateFieldSync(column, draft[column.name], locale).filter((item) => item.key === 'required');
      if (current.length || requiredErrors.length) output[column.name] = [...current, ...requiredErrors.filter((item) => !current.some((existing) => existing.key === item.key))];
      return output;
    }, {})
    : draftErrors;

  useEffect(() => {
    const allowedNames = new Set(draftModelFields.map((column) => column.name).filter(Boolean));
    setDraft((current) => Object.fromEntries(Object.entries(current).filter(([name]) => allowedNames.has(name))));
    setDraftErrors((current) => Object.fromEntries(Object.entries(current).filter(([name]) => allowedNames.has(name))));
  }, [field.id, field.tableColumns, field.tableRows]);

  const changeDraft = (name: string, next: unknown) => {
    if (!name) return;
    setDraft((current) => ({ ...current, [name]: next }));
    setDraftErrors((current) => {
      if (!current[name]) return current;
      const copy = { ...current };
      delete copy[name];
      return copy;
    });
  };

  const add = async () => {
    if (disabled) return;
    if (atMaximumRows) { notify(field.validation?.maximumRowsMessage || copy.maximumRows(maximumRows ?? 0), 'warning'); return; }
    const nextErrors: Record<string, ValidationError[]> = {};
    for (const column of draftModelFields) {
      const columnErrors = validateFieldSync(column, draft[column.name], locale);
      const rawValue = draft[column.name];
      const empty = rawValue == null || String(rawValue).trim() === '';
      if (column.type === 'simplecaptcha' && !empty && !columnErrors.length) {
        try {
          const answer = normalizeSimpleCaptchaInput(String(rawValue), resolveSimpleCaptchaMode(column));
          const state = getSimpleCaptchaChallenge(column.id, answer);
          if (!state.challengeId) columnErrors.push({ key: 'captcha', message: copy.captchaExpired });
          else if (!state.verified) {
            const valid = await validateCaptcha(answer, state.challengeId);
            if (valid) markSimpleCaptchaVerified(column.id, state.challengeId, answer);
            else columnErrors.push({ key: 'captcha', message: copy.captchaIncorrect });
          }
        } catch {
          columnErrors.push({ key: 'captcha', message: copy.captchaUnavailable });
        }
      }
      if (columnErrors.length) nextErrors[column.name] = columnErrors;
    }
    if (Object.keys(nextErrors).length) {
      setDraftErrors(nextErrors);
      notify(copy.fixTableRowErrors, 'warning');
      return;
    }
    const row: Record<string, unknown> = {};
    for (const column of columns) row[column.name] = draft[column.name] ?? (column.type === 'checkbox' ? 'False' : '');
    onChange([...rows, row]);
    setDraft({});
    setDraftErrors({});
    for (const column of draftModelFields) {
      if (column.type === 'simplecaptcha') requestSimpleCaptchaRefresh(column.id);
    }
  };

  const moveRow = (from: number, to: number) => {
    if (disabled || to < 0 || to >= rows.length) return;
    const next = [...rows];
    const [item] = next.splice(from, 1);
    if (!item) return;
    next.splice(to, 0, item);
    onChange(next);
  };

  const duplicateRow = (rowIndex: number) => {
    if (disabled) return;
    if (atMaximumRows) { notify(field.validation?.maximumRowsMessage || copy.maximumRows(maximumRows ?? 0), 'warning'); return; }
    const row = rows[rowIndex];
    if (!row) return;
    const next = [...rows];
    next.splice(rowIndex + 1, 0, structuredClone(row));
    onChange(next);
  };

  const tableAction = (action: NonNullable<FormField['buttonAction']>) => {
    if (action === 'submit') void add();
    else onAction?.(action);
  };

  return <div className={`df-table-field df-table-mobile-${field.tableMobileMode ?? 'cards'}`}>
    {!readOnly ? <div className="df-table-entry-area">
      {directColumns.length ? <div className="df-table-inputs">
        {directColumns.map((column) => <div key={column.id} className={`form-group df-form-group ${isFileUploadField(column) ? 'df-fileupload-field df-table-file-column' : ''} ${displayedDraftErrors[column.name]?.length ? 'has-error df-has-error' : ''}`}>
          {hasVisibleFieldTitle(column) ? <label title={normalizedFieldTitle(column)}>{renderFieldLabel(column)}</label> : null}
          <TableDraftInput field={column} value={draft[column.name]} errors={displayedDraftErrors[column.name]} disabled={disabled} onChange={(next) => changeDraft(column.name, next)} />
        </div>)}
      </div> : null}
      {field.tableRows?.length ? <TableDraftRows rows={field.tableRows} tableId={field.id} tabName={tabName} draft={draft} values={values} errors={displayedDraftErrors} disabled={disabled} requiredByTable={tableRequiresCompleteRows} onDraftChange={changeDraft} onAction={tableAction} /> : null}
      <div className="df-table-entry-actions"><button type="button" className="btn btn-info" disabled={disabled || !columns.length || atMaximumRows} onClick={() => void add()}>{copy.addRow}</button>{maximumRows != null ? <small>{copy.rowCount(rows.length, maximumRows)}</small> : null}</div>
    </div> : null}
    <div className="table-responsive">
      <table className={`table table-bordered table-striped table-condensed table-sm df-responsive-data-table df-table-mobile-${field.tableMobileMode ?? 'cards'}`}>
        <thead><tr>{field.tableShowRowNumbers ?? true ? <th className="df-table-row-number">{copy.row}</th> : null}{columns.map((column) => <th key={column.id}>{column.title}</th>)}{!readOnly ? <th className="df-table-action-column">{copy.actions}</th> : null}</tr></thead>
        <tbody>
          {rows.map((row, rowIndex) => <tr key={rowIndex}>
            {field.tableShowRowNumbers ?? true ? <td className="df-table-row-number" data-label={copy.row}>{formatNumber(rowIndex + 1)}</td> : null}
            {columns.map((column) => <td key={column.id} data-label={column.title}>{renderTableValue(column, row[column.name], Boolean(readOnly), copy, secureSubmissionId, submissionFileAccess)}</td>)}
            {!readOnly ? <td className="df-table-row-actions" data-label={copy.actions}><div className="btn-group">
              {field.tableAllowReorder ?? true ? <><button type="button" className="btn btn-xs btn-default" disabled={disabled || rowIndex === 0} onClick={() => moveRow(rowIndex, rowIndex - 1)} aria-label={copy.moveRowUp(rowIndex + 1)}>↑</button><button type="button" className="btn btn-xs btn-default" disabled={disabled || rowIndex === rows.length - 1} onClick={() => moveRow(rowIndex, rowIndex + 1)} aria-label={copy.moveRowDown(rowIndex + 1)}>↓</button></> : null}
              {field.tableAllowDuplicate ?? true ? <button type="button" className="btn btn-xs btn-info" disabled={disabled || atMaximumRows} onClick={() => duplicateRow(rowIndex)}>{copy.duplicate}</button> : null}
              <button type="button" className="btn btn-xs btn-danger" disabled={disabled} onClick={() => onChange(rows.filter((_, index) => index !== rowIndex))} aria-label={copy.deleteRow(rowIndex + 1)}>{copy.delete}</button>
            </div></td> : null}
          </tr>)}
          {!rows.length ? <tr><td className="text-muted text-center" colSpan={Math.max(1, columns.length + (field.tableShowRowNumbers ?? true ? 1 : 0) + (readOnly ? 0 : 1))}>{copy.noRows}</td></tr> : null}
        </tbody>
      </table>
    </div>
  </div>;
}

declare global {
  interface Window {
    grecaptcha?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => number;
      reset: (id?: number) => void;
    };
  }
}

function RecaptchaField({ field, onChange, readOnly, interactionDisabled, controlId, className, style, attributes }: Pick<Props, 'field' | 'onChange' | 'readOnly' | 'interactionDisabled'> & {
  controlId: string;
  className?: string;
  style?: CSSProperties;
  attributes: Record<string, string | boolean | number>;
}) {
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const ref = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const disabledRef = useRef(Boolean(interactionDisabled));
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { disabledRef.current = Boolean(interactionDisabled); }, [interactionDisabled]);

  useEffect(() => {
    if (readOnly || !field.captchaPublicKey || !ref.current) return;
    let disposed = false;
    const render = () => {
      if (disposed || !ref.current || !window.grecaptcha) return;
      ref.current.innerHTML = '';
      window.grecaptcha.render(ref.current, {
        sitekey: field.captchaPublicKey,
        callback: (token: string) => { if (!disabledRef.current) onChangeRef.current(token); },
        'expired-callback': () => { if (!disabledRef.current) onChangeRef.current(''); },
        'error-callback': () => { if (!disabledRef.current) onChangeRef.current(''); }
      });
    };
    if (window.grecaptcha) render();
    else {
      const id = 'df-recaptcha-script';
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.async = true;
        script.defer = true;
        script.src = `https://www.google.com/recaptcha/api.js?render=explicit&hl=${encodeURIComponent(locale)}`;
        document.head.append(script);
      }
      script.addEventListener('load', render, { once: true });
    }
    return () => { disposed = true; };
  }, [field.captchaPublicKey, locale, readOnly]);
  return readOnly
    ? <div className="text-muted">{copy.captchaPreviewUnavailable}</div>
    : <div
      {...attributes}
      id={controlId}
      ref={ref}
      className={['df-recaptcha', className, interactionDisabled ? 'is-disabled' : ''].filter(Boolean).join(' ')}
      style={{ ...style, pointerEvents: interactionDisabled ? 'none' : undefined, opacity: interactionDisabled ? 0.65 : undefined }}
      aria-disabled={interactionDisabled || undefined}
    />;
}

function SimpleCaptchaField({ field, value, onChange, readOnly, interactionDisabled, controlId, className, style, attributes, describedBy }: Pick<Props, 'field' | 'value' | 'onChange' | 'readOnly' | 'interactionDisabled'> & {
  controlId: string;
  className?: string;
  style?: CSSProperties;
  attributes: Record<string, string | boolean | number>;
  describedBy?: string;
}) {
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const mode = resolveSimpleCaptchaMode(field);
  const length = resolveSimpleCaptchaLength(field);
  const digitStyle = resolveSimpleCaptchaDigitStyle(field);
  const visualStyle = resolveSimpleCaptchaVisualStyle(field);
  const [challengeId, setChallengeId] = useState(createSimpleCaptchaChallengeId);
  const buildUrl = useCallback((id: string) => getCaptchaUrl({
    challengeId: id,
    length,
    mode,
    digitStyle,
    visualStyle
  }), [digitStyle, length, mode, visualStyle]);
  const [src, setSrc] = useState(() => buildUrl(challengeId));
  const disabled = Boolean(readOnly || interactionDisabled || field.disabled);
  const normalizedValue = normalizeSimpleCaptchaInput(String(value ?? ''), mode);

  const refresh = useCallback(() => {
    const nextChallengeId = createSimpleCaptchaChallengeId();
    setChallengeId(nextChallengeId);
    registerSimpleCaptchaChallenge(field.id, nextChallengeId);
    updateSimpleCaptchaAnswer(field.id, nextChallengeId, '');
    setSrc(buildUrl(nextChallengeId));
    onChange('');
  }, [buildUrl, field.id, onChange]);

  useEffect(() => {
    registerSimpleCaptchaChallenge(field.id, challengeId);
    updateSimpleCaptchaAnswer(field.id, challengeId, normalizedValue);
    return () => clearSimpleCaptchaChallenge(field.id, challengeId);
  }, [challengeId, field.id, normalizedValue]);

  useEffect(() => {
    setSrc(buildUrl(challengeId));
    updateSimpleCaptchaAnswer(field.id, challengeId, '');
    if (normalizedValue) onChange('');
  // Refresh only when administrator-visible captcha settings change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildUrl]);

  useEffect(() => subscribeSimpleCaptchaRefresh(field.id, refresh), [field.id, refresh]);

  const inputLength = mode === 'math' ? 3 : length;
  const numeric = mode === 'digits' || mode === 'math';
  return <div className={`df-simple-captcha df-simple-captcha--${visualStyle}`}>
    <div className="df-simple-captcha__challenge">
      <img src={src} alt={copy.captchaImageAlt} draggable={false} />
      {!readOnly ? <button type="button" className="btn btn-default df-simple-captcha__refresh" disabled={disabled} onClick={refresh} aria-label={copy.captchaNewLabel} title={copy.captchaNewLabel}>↻ <span>{copy.captchaNew}</span></button> : null}
    </div>
    <input
      {...attributes}
      id={controlId}
      name={field.name}
      className={['form-control', 'df-simple-captcha__input', className].filter(Boolean).join(' ')}
      style={style}
      value={normalizedValue}
      onChange={(event) => {
        const next = normalizeSimpleCaptchaInput(event.target.value, mode).slice(0, inputLength);
        updateSimpleCaptchaAnswer(field.id, challengeId, next);
        onChange(next);
      }}
      disabled={disabled}
      autoComplete="off"
      autoCapitalize="characters"
      spellCheck={false}
      inputMode={numeric ? 'numeric' : 'text'}
      maxLength={inputLength}
      dir="ltr"
      placeholder={numeric ? copy.enterCode : copy.enterLatinCode}
      aria-describedby={describedBy}
    />
    {!readOnly ? <small className="help-block df-simple-captcha__hint">{copy.captchaDigitHint}</small> : null}
  </div>;
}

function formatLiveDate(field: FormField, now: Date, locale: UiLocale, culture: string): string {
  const common: Intl.DateTimeFormatOptions = locale === 'fa' ? { timeZone: 'Asia/Tehran' } : {};
  try {
    if (field.dateDisplay === 'date') return now.toLocaleDateString(culture, common);
    if (field.dateDisplay === 'time') return now.toLocaleTimeString(culture, common);
    if (field.dateDisplay === 'dateText') return now.toLocaleDateString(culture, { ...common, weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    if (field.dateDisplay === 'timeText') return now.toLocaleTimeString(culture, common);
    return now.toLocaleString(culture, common);
  } catch {
    return now.toLocaleString(culture);
  }
}

function LiveDateTime({ field, controlId, attributes, className, style }: { field: FormField; controlId: string; attributes: Record<string, string | boolean | number>; className?: string; style?: CSSProperties }) {
  const { locale, culture } = useUiLocale();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const hasSeconds = field.dateDisplay === 'time' || field.dateDisplay === 'timeText' || field.dateDisplay === 'datetime';
    const timer = window.setInterval(() => setNow(new Date()), hasSeconds ? 1000 : 60000);
    return () => window.clearInterval(timer);
  }, [field.dateDisplay]);
  return <p {...attributes} id={controlId} className={className} style={style}>{formatLiveDate(field, now, locale, culture)}</p>;
}

function NonInputFrame({ field, children }: { field: FormField; children: ReactNode }) {
  const customization = field.customization;
  return <div
    {...safeWrapperAttributes(customization?.wrapperAttributes)}
    className={['df-field-wrapper', field.type === 'text' && field.textDisplay === 'inline' ? 'df-field-wrapper--inline' : '', customization?.wrapperClass].filter(Boolean).join(' ')}
    style={parseInlineStyle(customization?.wrapperStyle)}
    data-field-name={field.name || undefined}
  >
    <CustomHtml html={customization?.beforeHtml} position="before" />
    {children}
    <CustomHtml html={customization?.afterHtml} position="after" />
  </div>;
}

function PanelRows({ rows, tabName, values, errorMap, readOnly, onFieldChange, onAction, interactionDisabled, secureSubmissionId = 0, submissionFileAccess = 'owner', omitVerificationFields = false }: {
  rows: FormRow[];
  tabName: string;
  values: FormValues;
  errorMap?: Record<string, ValidationError[]>;
  readOnly: boolean;
  onFieldChange?: (name: string, value: unknown) => void;
  onAction?: (action: NonNullable<FormField['buttonAction']>) => void;
  interactionDisabled: boolean;
  secureSubmissionId?: number;
  submissionFileAccess?: SubmissionFileAccess;
  omitVerificationFields?: boolean;
}) {
  return <>{rows.map((row) => <div className="row df-form-row df-panel-row" key={row.id}>
    {row.columns.map((column) => <div className={`col-md-${column.span} df-form-col df-col-span-${column.span}`} key={column.id}>
      {column.fields.map((child) => <FieldRenderer
        key={child.id}
        field={child}
        tabName={tabName}
        value={values[tabName]?.[child.name]}
        values={values}
        errors={errorMap?.[errorPath(tabName, child.name)]}
        errorMap={errorMap}
        readOnly={readOnly}
        onChange={(next) => onFieldChange?.(child.name, next)}
        onFieldChange={onFieldChange}
        onAction={onAction}
        interactionDisabled={interactionDisabled}
        secureSubmissionId={secureSubmissionId}
        submissionFileAccess={submissionFileAccess}
        omitVerificationFields={omitVerificationFields}
      />)}
      {column.rows?.length ? <PanelRows rows={column.rows} tabName={tabName} values={values} errorMap={errorMap} readOnly={readOnly} onFieldChange={onFieldChange} onAction={onAction} interactionDisabled={interactionDisabled} secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} omitVerificationFields={omitVerificationFields} /> : null}
    </div>)}
  </div>)}</>;
}

function panelStyles(field: FormField, base?: CSSProperties): { panel: CSSProperties | undefined; heading?: CSSProperties; body?: CSSProperties } {
  return {
    panel: field.panelColor ? { ...base, borderColor: field.panelColor } : base,
    heading: field.panelColor || field.panelTextColor ? { backgroundColor: field.panelColor, borderColor: field.panelColor, color: field.panelTextColor } : undefined,
    body: field.panelBodyColor ? { backgroundColor: field.panelBodyColor } : undefined
  };
}

export function FieldRenderer({ field, tabName, value, values, errors, errorMap, readOnly = false, onChange, onFieldChange, onAction, interactionDisabled = false, secureSubmissionId = 0, submissionFileAccess = 'owner', omitVerificationFields = false }: Props) {
  const { locale, culture } = useUiLocale();
  const copy = publicCopy(locale);
  const visible = evaluateVisibility(field.visibility, values);
  if (!visible || field.hidden) return null;
  if (omitVerificationFields && (field.type === 'captcha' || field.type === 'simplecaptcha')) return null;

  const customization = field.customization;
  const controlAttributes = safeControlAttributes(customization?.controlAttributes);
  const controlStyle = parseInlineStyle(customization?.controlStyle);
  const wrapperAttributes = safeWrapperAttributes(customization?.wrapperAttributes);
  const wrapperStyle = parseInlineStyle(customization?.wrapperStyle);
  const controlId = customization?.customId || `df-${tabName}-${field.name || field.id}`.replace(/[^A-Za-z0-9_:\-.]/g, '-');
  const errorId = `${controlId}-errors`;
  const describedBy = errors?.length ? errorId : undefined;
  const disabled = Boolean(readOnly || field.disabled || interactionDisabled);

  if (field.type === 'panel') {
    const styles = panelStyles(field, controlStyle);
    const hasTitle = hasVisibleFieldTitle(field);
    return <NonInputFrame field={field}><div {...controlAttributes} id={controlId} style={styles.panel} className={`panel card df-panel panel-${field.panelStyle ?? 'primary'} ${hasTitle ? '' : 'df-panel--without-heading'} ${errors?.length ? 'has-error df-has-error' : ''} ${controlClass(field)}`}>
      {hasTitle ? <div className="panel-heading card-header" style={styles.heading}>{renderFieldLabel(field, { readOnly })}</div> : null}
      <div className="panel-body card-body" style={styles.body}>
        <ValidationMessages id={errorId} errors={errors} />
        {field.children?.map((child) => <FieldRenderer
          key={child.id}
          field={child}
          tabName={tabName}
          value={values[tabName]?.[child.name]}
          values={values}
          errors={errorMap?.[errorPath(tabName, child.name)]}
          errorMap={errorMap}
          readOnly={readOnly}
          onChange={(next) => onFieldChange?.(child.name, next)}
          onFieldChange={onFieldChange}
          onAction={onAction}
          interactionDisabled={interactionDisabled}
          secureSubmissionId={secureSubmissionId}
          submissionFileAccess={submissionFileAccess}
          omitVerificationFields={omitVerificationFields}
        />)}
        {field.panelRows?.length ? <PanelRows rows={field.panelRows} tabName={tabName} values={values} errorMap={errorMap} readOnly={readOnly} onFieldChange={onFieldChange} onAction={onAction} interactionDisabled={interactionDisabled} secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} omitVerificationFields={omitVerificationFields} /> : null}
      </div>
    </div></NonInputFrame>;
  }
  if (field.type === 'text') { const alignment = resolveFieldAlignment(field, locale); return <NonInputFrame field={field}><div {...controlAttributes} id={controlId} style={{ ...controlStyle, color: field.textColor, textAlign: alignment }} className={[controlClass(field), `df-text-display-${field.textDisplay ?? 'block'}`, field.textSize === 'lead' ? 'lead df-text-size-lead' : '', `text-${alignment}`].filter(Boolean).join(' ')} dangerouslySetInnerHTML={{ __html: sanitizeCustomHtml(field.html || field.text || '') }} /></NonInputFrame>; }
  if (field.type === 'spacer') return <NonInputFrame field={field}><div {...controlAttributes} id={controlId} style={{ ...controlStyle, height: field.spacerHeight ?? 32 }} className={`df-spacer df-spacer--runtime ${controlClass(field)}`} aria-hidden="true" /></NonInputFrame>;
  if (field.type === 'image') {
    const source = field.imageBindingSource ? getValueByPath(values, field.imageBindingSource) : undefined;
    const url = source ? fileUrl(String(source), secureSubmissionId, submissionFileAccess) : field.imageUrl;
    return url ? <NonInputFrame field={field}><img {...controlAttributes} id={controlId} className={`${field.responsive ? 'img-responsive img-fluid' : ''} ${controlClass(field)}`} src={url} alt={field.imageAlt ?? ''} style={{ ...controlStyle, width: field.imageWidth, height: field.imageHeight }} /></NonInputFrame> : null;
  }
  if (field.type === 'display-label') {
    const source = getValueByPath(values, field.displayBinding?.source ?? '');
    return <NonInputFrame field={field}><p {...controlAttributes} id={controlId} className={controlClass(field)} style={controlStyle}><strong>{field.title}</strong>{field.title ? ': ' : ''}{applyDisplayFilter(source, field.displayBinding?.filter ?? 'none', locale, culture)}</p></NonInputFrame>;
  }
  if (field.type === 'display-datetime') return <NonInputFrame field={field}><LiveDateTime field={field} controlId={controlId} attributes={controlAttributes} className={controlClass(field)} style={controlStyle} /></NonInputFrame>;
  if (field.type === 'button') {
    return <NonInputFrame field={field}><button {...controlAttributes} id={controlId} style={controlStyle} type="button" className={`btn btn-${field.buttonStyle ?? 'primary'} ${controlClass(field)}`} disabled={readOnly || field.disabled || interactionDisabled} onClick={() => onAction?.(field.buttonAction ?? 'none')}>{field.text || field.title}</button></NonInputFrame>;
  }

  let control: ReactNode;
  if (field.type === 'textarea') {
    control = readOnly
      ? <ReadOnlyValue field={field} value={value} secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} />
      : <textarea {...controlAttributes} id={controlId} name={field.name} style={controlStyle} aria-invalid={Boolean(errors?.length)} aria-describedby={describedBy} className={controlClass(field, `form-control ${errors?.length ? 'is-invalid' : ''}`)} rows={3} value={String(value ?? '')} placeholder={field.placeholder} disabled={disabled} tabIndex={field.tabIndex} onChange={(event) => onChange(event.target.value)} />;
  } else if (field.type === 'select') {
    control = readOnly
      ? <ReadOnlyValue field={field} value={value} secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} />
      : field.lookupEnabled
        ? <LookupInput field={field} tabName={tabName} values={values} value={value} disabled={disabled} controlId={controlId} className={controlClass(field, `form-control ${errors?.length ? 'is-invalid' : ''}`)} style={controlStyle} attributes={controlAttributes} describedBy={describedBy} onChange={onChange} />
        : <select {...controlAttributes} id={controlId} name={field.name} style={controlStyle} aria-invalid={Boolean(errors?.length)} aria-describedby={describedBy} className={controlClass(field, `form-control form-select ${errors?.length ? 'is-invalid' : ''}`)} value={String(value ?? '')} disabled={disabled} tabIndex={field.tabIndex} onChange={(event) => onChange(event.target.value)}>
          <option value="">{field.placeholder || copy.selectPlaceholder}</option>
          {field.options?.map((option) => <option key={option.id} value={option.value} disabled={option.disabled}>{option.label}</option>)}
        </select>;
  } else if (field.type === 'checkbox') {
    const checked = value === true || String(value ?? '').toLowerCase() === 'true';
    control = readOnly
      ? <ReadOnlyValue field={field} value={value} secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} />
      : <div className={['checkbox', 'form-check', 'df-check', fieldClass(field)].filter(Boolean).join(' ')}><label className="form-check-label"><input {...controlAttributes} aria-label={!hasVisibleFieldTitle(field) ? (field.designerTitle || field.name || copy.choice) : undefined} id={controlId} name={field.name} className={['form-check-input', errors?.length ? 'is-invalid' : '', customization?.controlClass].filter(Boolean).join(' ')} style={controlStyle} type="checkbox" checked={checked} disabled={disabled} tabIndex={field.tabIndex} aria-invalid={Boolean(errors?.length)} aria-describedby={describedBy} onChange={(event) => onChange(event.target.checked ? 'True' : 'False')} /> {hasVisibleFieldTitle(field) ? <span>{renderFieldLabel(field)}</span> : null}</label></div>;
  } else if (field.type === 'radio') {
    control = readOnly
      ? <ReadOnlyValue field={field} value={value} secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} />
      : <div {...controlAttributes} id={controlId} style={controlStyle} className={`df-choice-list df-choice-list--radio ${controlClass(field)}`} role="radiogroup" aria-label={!hasVisibleFieldTitle(field) ? (field.designerTitle || field.name || copy.choices) : undefined} aria-invalid={Boolean(errors?.length)} aria-describedby={describedBy}>{field.options?.map((option, index) => <div className="radio form-check df-radio" key={option.id}><label className="form-check-label"><input className={errors?.length ? 'form-check-input is-invalid' : 'form-check-input'} type="radio" id={`${controlId}-${index}`} name={`${tabName}-${field.name}`} value={option.value} checked={String(value ?? '') === option.value} disabled={disabled || option.disabled} onChange={(event) => onChange(event.target.value)} /> <span>{option.label}</span></label></div>)}</div>;
  } else if (isFileUploadField(field)) {
    control = <FileUploadControl
      secureSubmissionId={secureSubmissionId}
      submissionFileAccess={submissionFileAccess}
      field={field}
      value={value}
      readOnly={readOnly}
      disabled={disabled}
      controlId={controlId}
      className={controlClass(field, `form-control df-file-input ${errors?.length ? 'is-invalid' : ''}`)}
      style={controlStyle}
      attributes={controlAttributes}
      describedBy={describedBy}
      onChange={onChange}
    />;

  } else if (field.type === 'table') {
    control = <div {...controlAttributes} id={controlId} style={controlStyle} className={controlClass(field)}><TableField field={field} value={value} tabName={tabName} values={values} errors={errors} readOnly={readOnly} interactionDisabled={interactionDisabled} onChange={onChange} onAction={onAction} secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} /></div>;
  } else if (field.type === 'captcha') {
    control = <RecaptchaField field={field} onChange={onChange} readOnly={readOnly} interactionDisabled={interactionDisabled || field.disabled} controlId={controlId} className={controlClass(field)} style={controlStyle} attributes={controlAttributes} />;
  } else if (field.type === 'simplecaptcha') {
    control = <SimpleCaptchaField field={field} value={value} onChange={onChange} readOnly={readOnly} interactionDisabled={interactionDisabled} controlId={controlId} className={controlClass(field)} style={controlStyle} attributes={controlAttributes} describedBy={describedBy} />;
  } else {
    if (field.type === 'input' && field.calculationFormula !== undefined) {
      control = <CalculatedFieldInput field={field} tabName={tabName} values={values} value={value} readOnly={readOnly} controlId={controlId} className={controlClass(field, `form-control ${errors?.length ? 'is-invalid' : ''}`)} style={controlStyle} attributes={controlAttributes} describedBy={describedBy} onChange={onChange} />;
    } else if (readOnly) {
      control = <ReadOnlyValue field={field} value={value} secureSubmissionId={secureSubmissionId} submissionFileAccess={submissionFileAccess} />;
    } else {
      const special = field.inputType === 'tdate' || field.inputType === 'ttime' || field.inputType === 'tdatetime';
      const preset = getInputPreset(field.inputPreset);
      const numeric = field.inputType === 'number';
      const mobile = field.inputType === 'tel' || field.inputPreset === 'iranMobile';
      const url = field.inputType === 'url';
      const forceLtr = field.inputPreset === 'iranCardNumber' || field.inputPreset === 'iranIban';
    const placeholder = field.placeholder || (field.inputType === 'tdate'
      ? '1405/01/01'
      : field.inputType === 'ttime'
        ? '12:30:00'
        : field.inputType === 'tdatetime'
          ? '1405/01/01 12:30:00'
          : mobile
            ? '09123456789'
            : url
              ? 'example.ir'
              : undefined);

    const input = special
      ? <PersianDateTimeInput
        id={controlId}
        name={field.name}
        kind={field.inputType as 'tdate' | 'ttime' | 'tdatetime'}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        tabIndex={field.tabIndex}
        className={controlClass(field, `form-control ${errors?.length ? 'is-invalid' : ''}`)}
        style={controlStyle}
        attributes={controlAttributes}
        ariaInvalid={Boolean(errors?.length)}
        describedBy={describedBy}
        addonLeft={field.addonLeft}
        addonRight={field.addonRight}
        onChange={(next) => onChange(next)}
      />
      : <input
        {...controlAttributes}
        id={controlId}
        name={field.name}
        style={controlStyle}
        className={controlClass(field, `form-control ${forceLtr ? 'dir-ltr df-preset-ltr' : ''} ${errors?.length ? 'is-invalid' : ''}`)}
        dir={forceLtr ? 'ltr' : undefined}
        type={numeric ? 'number' : field.inputType === 'email' ? 'email' : mobile ? 'tel' : field.inputType === 'password' ? 'password' : 'text'}
        min={numeric ? field.validation?.minValue : undefined}
        max={numeric ? field.validation?.maxValue : undefined}
        step={numeric ? field.validation?.step ?? 'any' : undefined}
        inputMode={preset?.inputMode ?? (numeric ? 'decimal' : mobile ? 'tel' : url ? 'url' : undefined)}
        maxLength={preset?.displayMaxLength ?? preset?.maxLength ?? (mobile ? 14 : undefined)}
        autoComplete={preset?.autoComplete ?? (mobile ? 'tel' : url ? 'url' : field.inputType === 'password' ? 'current-password' : undefined)}
        spellCheck={url ? false : undefined}
        value={field.inputPreset ? displayPresetInput(value, field.inputPreset) : numeric ? normalizeNumericText(value) : String(value ?? '')}
        placeholder={placeholder}
        disabled={disabled}
        tabIndex={field.tabIndex}
        aria-invalid={Boolean(errors?.length)}
        aria-describedby={describedBy}
        onBlur={(event) => {
          if (field.inputPreset) onChange(normalizePresetInput(event.currentTarget.value, field.inputPreset));
          else if (mobile) onChange(normalizeIranianMobile(event.currentTarget.value));
          else if (url && event.currentTarget.value.trim()) onChange(normalizeHttpUrl(event.currentTarget.value));
        }}
        onChange={(event) => onChange(field.inputPreset
          ? normalizePresetInput(event.target.value, field.inputPreset)
          : numeric
            ? normalizeNumericText(event.target.value)
            : mobile
              ? sanitizeIranianMobileInput(event.target.value)
              : applyLegacyMask(event.target.value, field.validation?.mask))}
      />;

      control = special
        ? input
        : field.addonLeft || field.addonRight ? <div className="input-group df-input-group">
          {field.addonLeft ? <span className="input-group-addon input-group-text">{field.addonLeft}</span> : null}
          {input}
          {field.addonRight ? <span className="input-group-addon input-group-text">{field.addonRight}</span> : null}
        </div> : input;
    }
  }

  const hideExternalLabel = (field.type === 'checkbox' && !readOnly) || !hasVisibleFieldTitle(field);
  return <div
    {...wrapperAttributes}
    className={['form-group', 'df-form-group', isFileUploadField(field) ? 'df-fileupload-field' : '', errors?.length ? 'has-error df-has-error' : '', customization?.wrapperClass].filter(Boolean).join(' ')}
    style={wrapperStyle}
    data-field-name={field.name}
    data-field-type={field.type}
  >
    <CustomHtml html={customization?.beforeHtml} position="before" />
    {!hideExternalLabel ? <label htmlFor={controlId} title={normalizedFieldTitle(field)}>{renderFieldLabel(field, { readOnly })}</label> : null}
    {control}
    <ValidationMessages id={errorId} errors={errors} />
    <CustomHtml html={customization?.afterHtml} position="after" />
  </div>;
}
